/* app/taxonomy-attribution.jsx - which part of the website a category or tag
   belongs to, and the admission that the answer was worked out rather than
   declared.

   WHY THIS EXISTS. One WordPress install can serve two MarketLens sites,
   partitioned by a non-public taxonomy whose slug is a cuid. Content IS
   partitioned and taxonomy is NOT: there is one list of categories and tags for
   the whole install, shared by both parts, so a category could not say which
   part of the website it belonged to. Nothing in the product answered that
   question before this.

   TWO FACTS, TWO COLUMNS, NEVER ONE. The server keeps them apart and so does
   this screen:

     USE is derived. Which parts' posts actually carry the term, counted from
     the posts themselves through each part's own directory path. A term created
     in one part and then used by twenty posts in the other belongs to both, and
     the row says both.

     ORIGIN is stored. Which part a term was CREATED through, recorded by the
     install at the moment it happened. It is the only evidence that exists
     about a term no post carries yet, and it is not an answer to who uses it.

   A row where the two disagree is shown as disagreeing. Folding one into the
   other would be the whole defect this screen was built to avoid.

   AND IT SAYS IT IS DERIVED, ON THE PAGE. The house rule, written down after
   it went wrong four times: a fact is declared, never inferred, and where
   something IS inferred the screen says so. The line itself carries "Worked
   out, not declared", the drawer opens with the server's own sentence about
   what it was worked out from, and every class carries the sentence that says
   how it was decided, including the two that are admissions rather than
   findings.

   FOUR CLASSES AND ONE ADMISSION, and they are five rather than three for the
   same reason the Coverage Map's bands are four: "used by nothing" and "nobody
   could look" produce the same emptiness and mean opposite things. Used by
   nothing is the interesting one, because a term nothing carries has no use to
   derive anything from and its stored origin is all there is.

   Reads GET /api/taxonomy/attribution?site_id&type. Read only: nothing on this
   screen writes anything, to MarketLens or to WordPress.

   WIRING OWED. The natural homes for this line are the Categories screen, the
   Tags screen and the Taxonomy Library, all three of which live in
   app/content.jsx, and the module list lives in main.jsx. Both files were being
   edited by other agents when this was written, so neither was touched. Two one
   line additions finish it, and they are named in CHANGELOG.md and in
   docs/UI-FUNCTIONAL-SPEC.md rather than left for somebody to rediscover. */
import { api } from '../lib/api.js';

(function () {
  const { useState, useEffect } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  /* A line that opens a drawer, resolved at RENDER time so this module does not
     depend on script order (the same way coverage-map.jsx reaches it). If the
     drawer module were missing the body renders inline rather than the line
     vanishing with it. */
  const DetailLine = (p) => {
    const C = window.MlReportDrawer && window.MlReportDrawer.DetailLine;
    return C ? React.createElement(C, p) : React.createElement("div", { className: "ds-card" }, p.children);
  };

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /* The five states, each with the label a person reads and the tone it earns.
     `not_known` is deliberately not a shade of `unused`: one is a finding, the
     other is an admission that the evidence ran out. */
  const CLASS_LABEL = {
    one_section:           { label: "Used by one part",        tone: "success" },
    several_sections:      { label: "Used by several parts",   tone: "success" },
    unused:                { label: "Used by nothing",         tone: "warning" },
    used_outside_sections: { label: "Used outside every part", tone: "danger"  },
    not_known:             { label: "Not known",               tone: "neutral" },
  };

  /* The design system's own badge, the same one the Coverage Map's bands use,
     so five states of one kind of answer look alike across two screens. */
  function classBadge(termClass) {
    const { Badge } = window.HuddleDesignSystem_775113;
    const c = CLASS_LABEL[termClass] || CLASS_LABEL.not_known;
    return <Badge tone={c.tone} dot={termClass !== "not_known"}>{c.label}</Badge>;
  }
  const CLASS_ORDER = ["several_sections", "one_section", "used_outside_sections", "unused", "not_known"];

  /* The one-line outcome. Derived from the same payload the body reads, never
     from anywhere else, so the line and the drawer cannot disagree. */
  function titleOf(data, type) {
    const noun = type === "tags" ? "tags" : "categories";
    if (!data) return `Which part of the website these ${noun} belong to`;
    const c = data.classes || {};
    const shared = c.several_sections || 0;
    const unused = c.unused || 0;
    const unknown = c.not_known || 0;
    if (!data.shared_pool_present && !shared) {
      return `${plural(data.installs.reduce((n, i) => n + (i.terms_total || 0), 0), noun.slice(0, -1), noun)}, `
        + `each in one part's own list` + (unused ? `, ${unused} used by nothing` : "");
    }
    return `${plural(shared, "shared " + noun.slice(0, -1), "shared " + noun)} used by more than one part of this `
      + `website` + (unused ? `, ${plural(unused, "used by nothing", "used by nothing")}` : "")
      + (unknown ? `, ${unknown} not known` : "");
  }

  /* THE LABEL. Rendered from the server's own sentence rather than a second
     wording invented here: two wordings of one caveat drift apart, and the one
     that drifts is always the one on screen. */
  function DerivedNote({ data }) {
    return (
      <div className="ds-nudge ds-nudge--todo" style={{ marginBottom: 12 }}>
        <span className="ds-card__icon"><I name="compass" size={15} /></span>
        <span className="ds-nudge__text">
          <b>Worked out, not declared.</b>{" "}{data.derived_why}
        </span>
      </div>
    );
  }

  /* What was read, per part, including the part that could not be. A part whose
     posts could not be read is NOT a part that uses nothing, and a part with no
     CMS takes no part in this at all, which is different again. */
  function WhatWasRead({ data }) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div className="ds-card__note">What this was read from</div>
        {data.installs.map(inst => (
          <div key={inst.install_key} style={{ marginBottom: 8 }}>
            {inst.pool_shared && (
              <p className="bi-subempty" style={{ margin: "0 0 4px" }}>{inst.pool_why}</p>
            )}
            {(inst.sections || []).map(s => (
              <p key={s.section_id} className="bi-subempty" style={{ margin: "0 0 2px" }}>
                {s.read_ok && s.read_complete
                  ? s.why
                  : <b>{s.section_name}: could not be read all the way through ({(s.errors || []).join("; ") || "no reason given"}), so nothing here is called unused on its account.</b>}
                {s.posts_total != null && s.read_ok ? ` ${s.posts_read} of ${s.posts_total} read.` : ""}
              </p>
            ))}
            {inst.why && <p className="bi-subempty" style={{ margin: 0 }}><b>{inst.why}</b></p>}
            {inst.terms_why && <p className="bi-subempty" style={{ margin: 0 }}><b>{inst.terms_why}</b></p>}
            {inst.origin_why && <p className="bi-subempty" style={{ margin: "4px 0 0" }}>{inst.origin_why}</p>}
          </div>
        ))}
        {(data.excluded || []).map(x => (
          <p key={x.section_id} className="bi-subempty" style={{ margin: "0 0 2px" }}>{x.why}</p>
        ))}
        {data.shared_pool_unknown && (
          <p className="bi-subempty" style={{ margin: "4px 0 0" }}><b>
            One part of this website has not recorded which install it belongs to, so whether it shares a list of
            categories with another part is not known. Nothing was merged on a guess.
          </b></p>
        )}
      </div>
    );
  }

  /* One term: its use on the left, its origin on the right, and the two never
     folded. The origin column is the only DECLARED thing on this screen. */
  function TermRow({ row }) {
    return (
      <tr>
        <td>
          <b>{row.name}</b>
          <div className="bi-subempty">{row.slug}</div>
        </td>
        <td>
          {classBadge(row.term_class)}
          <div className="bi-subempty">{row.class_why}</div>
          {row.used_in.length > 0 && (
            <div className="bi-subempty">
              {row.used_in.map(u => `${u.section_name}: ${u.published} published${u.other ? `, ${u.other} not published` : ""}`).join(" · ")}
            </div>
          )}
        </td>
        <td>
          <div className="bi-subempty">{row.origin_label}</div>
          {row.origin_and_use_disagree && (
            <div className="bi-subempty"><b>
              Created in one part and used in another. Both are true: the first says where it came from, the second
              says who uses it, and neither answers the other's question.
            </b></div>
          )}
        </td>
      </tr>
    );
  }

  /**
   * The report, folded to one line.
   *
   * `site` is the active connection; the report is read for its whole website,
   * because the question is about the OTHER parts and a part filter would turn
   * the only interesting class into the part that was asked about.
   */
  function TermAttribution({ site, type = "categories" }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      if (siteId == null) return;
      let dead = false;
      setBusy(true); setErr(null);
      api(`/taxonomy/attribution?site_id=${siteId}&type=${type === "tags" ? "tags" : "categories"}`)
        .then(d => { if (!dead) setData(d); })
        .catch(e => { if (!dead) setErr(e.message || "the report could not be read"); })
        .finally(() => { if (!dead) setBusy(false); });
      return () => { dead = true; };
    }, [siteId, type]);

    if (siteId == null) return null;
    if (busy && !data) return null;
    if (err) {
      return (
        <DetailLine title="Which part of the website these terms belong to could not be read" tone="problem">
          <p>{err}</p>
        </DetailLine>
      );
    }
    if (!data) return null;

    // The install travels with the row: a term id means something only inside
    // its own pool, so two installs that both hold term 7 are two terms and a
    // key on the id alone would collapse them into one row.
    const rows = data.installs.flatMap(i => (i.rows || []).map(r => ({ ...r, install_key: i.install_key })));
    const order = (r) => CLASS_ORDER.indexOf(r.term_class);
    const sorted = [...rows].sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));

    return (
      <DetailLine
        title={titleOf(data, type)}
        tone={data.classes.used_outside_sections ? "problem" : (data.classes.not_known ? "todo" : "opportunity")}
        subtitle="Worked out from which posts carry the term"
      >
        <DerivedNote data={data} />
        <WhatWasRead data={data} />

        <div className="ds-card__note">How many of each</div>
        <div style={{ marginBottom: 16 }}>
          {CLASS_ORDER.map(k => (
            <p key={k} className="bi-subempty" style={{ margin: "0 0 2px" }}>
              {CLASS_LABEL[k].label}: <b>{data.classes[k] || 0}</b>
            </p>
          ))}
        </div>

        {sorted.length === 0
          ? <p className="bi-subempty">There are no terms in this list to attribute.</p>
          : (
            <div className="kw-table-wrap">
            <table className="kw-table">
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Used by (worked out)</th>
                  <th>Created through (recorded)</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => <TermRow key={`${r.install_key || ""}:${r.id}`} row={r} />)}
              </tbody>
            </table>
            </div>
          )}
      </DetailLine>
    );
  }

  window.MlTermAttribution = { TermAttribution, CLASS_LABEL, CLASS_ORDER };
})();
